import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUsers, FaHandshake, FaEnvelope, FaChartLine, 
  FaEye, FaEdit, FaCog, FaUserShield, FaBlog,
  FaHome, FaServicestack, FaInfoCircle, FaChalkboardTeacher,
  FaArrowUp, FaArrowDown, FaDollarSign, FaClock, FaChevronDown
} from 'react-icons/fa';
import AdminSidebar from '../Components/Admin/AdminSidebar';
import AdminUpskill from '../Components/Admin/ContentEdit/AdminUpskill';
import AdminInquiry from '../Components/Admin/Inquiry/AdminInquiry';
import AdmnInfluncerReq from '../Components/Admin/AdmnInfluncerReq';
import AdminBlog from '../Components/Admin/AdminBlog';
import BannerEdit from '../Components/Admin/BannerEdit';
import AdminHireMe from '../Components/Admin/AdminHireMe';
import AdminPremiumInfluencer from '../Components/Admin/AdminPremiumInfluencer';
import AdminInfluencer from '../Components/Admin/AdminInfluencer';
import ComingSoon from '../Components/Admin/ComingSoon';
import AdminAdmins from '../Components/Admin/AdminAdmins';
import AdminPageHeader from '../Components/Admin/AdminPageHeader';
import { API_ENDPOINTS } from '../utils/GlobalAPI';

const AdminHomepage = () => {
  // Initialize selected from localStorage or default to Dashboard (validating permissions)
  const [selected, setSelected] = useState(() => {
    const saved = localStorage.getItem('adminSelectedPage') || 'Dashboard';
    const email = localStorage.getItem('adminEmail');
    const role = localStorage.getItem('adminRole') || 'subadmin';
    const isSuper = role === 'superadmin' || email === 'admin1@gmail.com';
    let modules = [];
    try {
      modules = JSON.parse(localStorage.getItem('adminAllowedModules')) || [];
    } catch (e) {}

    const isAllowed = isSuper || saved === 'Dashboard' || (saved !== 'Admins' && modules.includes(saved));
    return isAllowed ? saved : 'Dashboard';
  });
  const [dashboardData, setDashboardData] = useState({
    influencers: { total: 0, pending: 0, approved: 0, rejected: 0 },
    hireRequests: { total: 0, recent: 0 },
    inquiries: { total: 0, pending: 0, thisMonth: 0 },
    blogs: { total: 0 },
    banners: { total: 0 }
  });
  const [loading, setLoading] = useState(true);

  // Check if admin is logged in and validate page permissions
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      window.location.href = '/admin';
      return;
    }

    const email = localStorage.getItem('adminEmail');
    const role = localStorage.getItem('adminRole') || 'subadmin';
    const isSuper = role === 'superadmin' || email === 'admin1@gmail.com';
    let modules = [];
    try {
      modules = JSON.parse(localStorage.getItem('adminAllowedModules')) || [];
    } catch (e) {}

    const isAllowed = isSuper || selected === 'Dashboard' || (selected !== 'Admins' && modules.includes(selected));
    if (!isAllowed) {
      setSelected('Dashboard');
    }
  }, [selected]);

  // Save selected page to localStorage whenever it changes
  useEffect(() => {
    if (selected) {
      localStorage.setItem('adminSelectedPage', selected);
    }
  }, [selected]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        console.log('Fetching dashboard data...');
        console.log('API Endpoints:', {
          INFLUENCER: API_ENDPOINTS.INFLUENCER,
          HIRE_REQUESTS: API_ENDPOINTS.HIRE_REQUESTS,
          CONTACT: API_ENDPOINTS.CONTACT,
          BLOGS: API_ENDPOINTS.BLOGS,
          BANNERS: API_ENDPOINTS.BANNERS
        });

        const [influencersRes, hireRes, inquiriesRes, blogsRes, bannersRes] = await Promise.all([
          fetch(API_ENDPOINTS.INFLUENCER),
          fetch(API_ENDPOINTS.HIRE_REQUESTS),
          fetch(API_ENDPOINTS.CONTACT),
          fetch(API_ENDPOINTS.BLOGS),
          fetch(API_ENDPOINTS.BANNERS)
        ]);

        // Check response status
        console.log('Response statuses:', {
          influencers: influencersRes.status,
          hireRequests: hireRes.status,
          inquiries: inquiriesRes.status,
          blogs: blogsRes.status,
          banners: bannersRes.status
        });

        const [influencers, hireRequests, inquiries, blogs, banners] = await Promise.all([
          influencersRes.json(),
          hireRes.json(),
          inquiriesRes.json(),
          blogsRes.json(),
          bannersRes.json()
        ]);

        console.log('Raw data received:', { influencers, hireRequests, inquiries, blogs, banners });

        setDashboardData({
          influencers: {
            total: influencers.length,
            pending: influencers.filter(i => i.status === 'pending').length,
            approved: influencers.filter(i => i.status === 'approved').length,
            rejected: influencers.filter(i => i.status === 'rejected').length
          },
          hireRequests: {
            total: hireRequests.length,
            recent: hireRequests.filter(h => {
              const date = new Date(h.createdAt || h.date || Date.now());
              const now = new Date();
              const diffTime = Math.abs(now - date);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              return diffDays <= 7;
            }).length
          },
          inquiries: {
            total: inquiries.length,
            pending: inquiries.filter(i => i.status === 'pending').length,
            thisMonth: inquiries.filter(i => {
              const date = new Date(i.createdAt);
              const now = new Date();
              return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
            }).length
          },
          blogs: { total: blogs.length },
          banners: { total: banners.length }
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set default values on error
        setDashboardData({
          influencers: { total: 0, pending: 0, approved: 0, rejected: 0 },
          hireRequests: { total: 0, recent: 0 },
          inquiries: { total: 0, pending: 0, thisMonth: 0 },
          blogs: { total: 0 },
          banners: { total: 0 }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const renderContent = () => {
    if (selected === 'Dashboard') return <DashboardContent data={dashboardData} loading={loading} onSelect={setSelected} />;
    if (selected === 'UpSkill') return <AdminUpskill />;
    if (selected === 'Inquiry') return <AdminInquiry />;
    if (selected === 'Influencer Request') return <AdmnInfluncerReq />;
    if (selected === 'HireMeRequest') return <AdminHireMe />;
    if (selected === 'Blog') return <AdminBlog />;
    if (selected === 'Homepage') return <BannerEdit />;
    if (selected === 'Premium Influencers') return <AdminPremiumInfluencer />;
    if (selected === 'Manage Influencers') return <AdminInfluencer />;
    
    // Coming Soon pages for unimplemented features
    if (selected === 'Services') return <ComingSoon title="Services Management" subtitle="Manage and edit service offerings" />;
    if (selected === 'About Us') return <ComingSoon title="About Us Management" subtitle="Edit company information and team details" />;
    if (selected === 'Admins') return <AdminAdmins />;
    if (selected === 'Users') return <ComingSoon title="User Management" subtitle="View and manage platform users" />;
    if (selected === 'Settings') return <ComingSoon title="Settings" subtitle="Configure platform settings and preferences" />;
    
    return <DashboardContent data={dashboardData} loading={loading} onSelect={setSelected} />;
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      <AdminSidebar onSelect={setSelected} activeOption={selected} />

      <main className="ml-64 flex-1 overflow-y-auto" style={{ maxHeight: '100vh' }}>
        {renderContent()}
      </main>
    </div>
  );
};

// Dashboard Content Component
const DashboardContent = ({ data, loading, onSelect }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }
  const quickActions = [
    {
      title: 'Influencer Requests',
      description: 'Manage creator applications',
      icon: <FaUsers className="w-6 h-6" />,
      color: 'bg-gradient-to-br from-orange-50 to-pink-50 text-orange-600',
      count: data.influencers.pending,
      onClick: () => onSelect('Influencer Request')
    },
    {
      title: 'Hire Requests',
      description: 'View collaboration requests',
      icon: <FaHandshake className="w-6 h-6" />,
      color: 'bg-green-50 text-green-600',
      count: data.hireRequests.recent,
      onClick: () => onSelect('HireMeRequest')
    },
    {
      title: 'Contact Inquiries',
      description: 'Handle customer inquiries',
      icon: <FaEnvelope className="w-6 h-6" />,
      color: 'bg-gradient-to-br from-pink-50 to-purple-50 text-pink-600',
      count: data.inquiries.pending,
      onClick: () => onSelect('Inquiry')
    },
    {
      title: 'Content Management',
      description: 'Edit website content',
      icon: <FaEdit className="w-6 h-6" />,
      color: 'bg-orange-50 text-orange-600',
      count: data.blogs.total + data.banners.total,
      onClick: () => onSelect('Blog')
    }
  ];

  const statsCards = [
    {
      title: 'Total Influencers',
      value: data.influencers.total,
      icon: <FaUsers className="w-6 h-6" />,
      color: 'bg-gradient-to-br from-orange-50 to-pink-50 text-orange-600',
      change: `+${data.influencers.approved}`,
      changeType: 'positive',
      subtitle: 'registered'
    },
    {
      title: 'Pending Requests',
      value: data.influencers.pending,
      icon: <FaClock className="w-6 h-6" />,
      color: 'bg-orange-50 text-orange-600',
      change: 'Needs Review',
      changeType: 'neutral',
      subtitle: 'awaiting approval'
    },
    {
      title: 'Hire Requests',
      value: data.hireRequests.total,
      icon: <FaHandshake className="w-6 h-6" />,
      color: 'bg-green-50 text-green-600',
      change: `+${data.hireRequests.recent}`,
      changeType: 'positive',
      subtitle: 'this week'
    },
    {
      title: 'Inquiries',
      value: data.inquiries.total,
      icon: <FaEnvelope className="w-6 h-6" />,
      color: 'bg-gradient-to-br from-pink-50 to-purple-50 text-pink-600',
      change: `+${data.inquiries.thisMonth}`,
      changeType: 'positive',
      subtitle: 'this month'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50/50 min-h-screen">
      <AdminPageHeader
        title="Admin Control Center"
        subtitle="Overview of platform performance, inquiries, and applications"
        icon={<FaChartLine className="w-6 h-6" />}
      />

      <div className="pt-32 p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm flex items-center justify-between"
        >
          <div>
            <h1 className="text-xl font-bold text-slate-800 mb-1">
              Hello, Admin
            </h1>
            <p className="text-slate-500 text-sm font-medium">
              Track team progress and operational metrics. You have almost reached your target goal!
            </p>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {statsCards.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2.5 rounded-xl ${stat.color} border`}>
                  {stat.icon}
                </div>
                <div className={`flex items-center text-xs font-semibold ${
                  stat.changeType === 'positive' ? 'text-emerald-600' : 'text-rose-600'
                }`}>
                  {stat.changeType === 'positive' ? <FaArrowUp className="w-3 h-3 mr-0.5" /> : <FaArrowDown className="w-3 h-3 mr-0.5" />}
                  {stat.change}
                </div>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-1">{stat.value}</h3>
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">{stat.title}</p>
              <p className="text-[11px] text-slate-400 mt-1">{stat.subtitle}</p>
            </div>
          ))}
        </motion.div>

        {/* Recent Activity Summary */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-600">System Overview</h2>
            <div className="flex items-center space-x-1.5 text-slate-400 text-xs font-semibold">
              <span>Last 30 days</span>
              <FaChevronDown className="w-3 h-3" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-5 bg-gradient-to-br from-orange-50/40 to-pink-50/40 border border-orange-100/30 rounded-2xl">
              <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent mb-1.5">{data.influencers.approved}</div>
              <div className="text-slate-700 text-sm font-bold mb-0.5">Approved Creators</div>
              <div className="text-xs text-slate-400 font-medium">Ready for collaboration</div>
            </div>
            <div className="text-center p-5 bg-gradient-to-br from-emerald-50/40 to-teal-50/40 border border-emerald-100/30 rounded-2xl">
              <div className="text-3xl font-bold text-emerald-600 mb-1.5">{data.hireRequests.recent}</div>
              <div className="text-slate-700 text-sm font-bold mb-0.5">Recent Hire Logs</div>
              <div className="text-xs text-slate-400 font-medium">This week</div>
            </div>
            <div className="text-center p-5 bg-gradient-to-br from-purple-50/40 to-indigo-50/40 border border-purple-100/30 rounded-2xl">
              <div className="text-3xl font-bold text-purple-600 mb-1.5">{data.inquiries.thisMonth}</div>
              <div className="text-slate-700 text-sm font-bold mb-0.5">Monthly Inquiries</div>
              <div className="text-xs text-slate-400 font-medium">Customer interest</div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-600">Quick Actions</h2>
            <span className="text-xs text-slate-400 font-medium">Manage your platform modules</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <motion.button
                key={index}
                whileHover={{ y: -2 }}
                onClick={action.onClick}
                className="p-5 rounded-2xl border border-slate-100 hover:border-slate-200 bg-slate-50/20 hover:bg-slate-50/50 hover:shadow-sm transition-all duration-200 text-left flex flex-col justify-between h-36"
              >
                <div className="flex items-start space-x-3">
                  <div className={`p-2.5 rounded-xl ${action.color} border flex-shrink-0`}>
                    {action.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-sm">{action.title}</h3>
                    <p className="text-[11px] text-slate-400 font-medium mt-0.5 leading-snug">{action.description}</p>
                  </div>
                </div>
                <div className="flex items-baseline justify-between mt-4 w-full">
                  <span className="text-2xl font-bold text-slate-800">{action.count}</span>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">records</span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminHomepage;
